import { useState, FormEvent } from "react";

export default function ContactSection() {
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageField = form.querySelector<HTMLTextAreaElement>("textarea[name='message']");
    if (messageField && messageField.value.length > MAX_CHARS) return;

    setFormState("submitting");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7fllsnimhg6tri2verg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormState("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  return (
    <section id="contact" className="w-full py-24 md:py-32" style={{ backgroundColor: "#f9f8f6" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-16">
          <span
            className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-gray-400 mb-5 border border-gray-200 px-3 py-1 rounded-full"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Get In Touch
          </span>
          <h2
            className="text-4xl md:text-5xl font-normal leading-tight text-gray-900"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Contact
            <span className="italic"> INTEROSMO P.C.</span>
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left: Contact Info */}
          <div className="w-full lg:w-2/5 flex-shrink-0">
            <div className="rounded-2xl p-8 bg-gray-900 text-white h-full flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  INTEROSMO P.C.
                </h3>
                <p className="text-white/50 text-sm mb-10 leading-6" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                  We&apos;re happy to answer your questions about our iOS products or explore potential collaborations.
                </p>

                <ul className="flex flex-col gap-6">
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-white/70 text-sm"></i>
                    </div>
                    <div>
                      <span className="block text-xs font-semibold tracking-widest uppercase text-white/40 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Address
                      </span>
                      <span className="text-sm text-white/80 leading-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                        6 Pefkon Metamorfosi
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0 mt-0.5">
                      <i className="ri-building-line text-white/70 text-sm"></i>
                    </div>
                    <div>
                      <span className="block text-xs font-semibold tracking-widest uppercase text-white/40 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                        City
                      </span>
                      <span className="text-sm text-white/80" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Attikis
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0 mt-0.5">
                      <i className="ri-mail-send-line text-white/70 text-sm"></i>
                    </div>
                    <div>
                      <span className="block text-xs font-semibold tracking-widest uppercase text-white/40 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Email
                      </span>
                      <span className="text-sm text-white/80" style={{ fontFamily: "'Inter', sans-serif" }}>
                        info@interosmopc.com
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0 mt-0.5">
                      <i className="ri-phone-line text-white/70 text-sm"></i>
                    </div>
                    <div>
                      <span className="block text-xs font-semibold tracking-widest uppercase text-white/40 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Number
                      </span>
                      <span className="text-sm text-white/80" style={{ fontFamily: "'Inter', sans-serif" }}>
                        +1 2295081216
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-3 gap-4">
                <div>
                  <span className="block text-xs font-semibold tracking-widest uppercase text-white/30 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Zip
                  </span>
                  <span className="text-sm text-white/70" style={{ fontFamily: "'Inter', sans-serif" }}>14451</span>
                </div>
                <div>
                  <span className="block text-xs font-semibold tracking-widest uppercase text-white/30 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Country
                  </span>
                  <span className="text-sm text-white/70" style={{ fontFamily: "'Inter', sans-serif" }}>Greece</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="w-full lg:w-3/5">
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-xs font-semibold text-gray-500 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="John Smith"
                    className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-xs font-semibold text-gray-500 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="subject" className="text-xs font-semibold text-gray-500 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  placeholder="How can we help?"
                  className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="message" className="text-xs font-semibold text-gray-500 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Message
                  </label>
                  <span className={`text-xs ${charCount > MAX_CHARS ? "text-red-500" : "text-gray-400"}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                    {charCount}/{MAX_CHARS}
                  </span>
                </div>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={MAX_CHARS}
                  placeholder="Tell us about your inquiry..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors resize-none"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>

              {formState === "success" && (
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-green-500 text-base"></i>
                  <span className="text-sm text-green-700" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Your message has been sent. We&apos;ll be in touch soon!
                  </span>
                </div>
              )}

              {formState === "error" && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-500 text-base"></i>
                  <span className="text-sm text-red-700" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Something went wrong. Please try again.
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={formState === "submitting" || charCount > MAX_CHARS}
                className="w-full bg-gray-900 text-white rounded-lg py-3.5 text-sm font-semibold tracking-wide hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {formState === "submitting" ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
